import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Descriptions,
  Tabs,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Switch,
  InputNumber,
  Popconfirm,
  Spin,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  EditOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import {
  useQualityPlan,
  useUpdateQualityPlan,
  useApproveQualityPlan,
  useQualityPlanItems,
  useCreateQualityPlanItem,
  useUpdateQualityPlanItem,
  useDeleteQualityPlanItem,
} from '../../hooks/useQuality';
import { QualityPlan, QualityPlanItem } from '../../services/quality.service';
import { QualityPlanApprovalWorkflow } from '../../components/quality/QualityPlanApprovalWorkflow';
import dayjs from 'dayjs';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';

const { Title, Text } = Typography;
const { TextArea } = Input;

const QualityPlanDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isItemModalVisible, setIsItemModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<QualityPlanItem | null>(null);
  const [form] = Form.useForm();

  const { data: qualityPlan, isLoading, refetch } = useQualityPlan(id || '');
  const { data: items = [], refetch: refetchItems } = useQualityPlanItems(id || '');
  const updateMutation = useUpdateQualityPlan();
  const approveMutation = useApproveQualityPlan();
  const createItemMutation = useCreateQualityPlanItem();
  const updateItemMutation = useUpdateQualityPlanItem();
  const deleteItemMutation = useDeleteQualityPlanItem();
  const { resolve } = useUserDisplayResolver();

  useEffect(() => {
    if (qualityPlan?.items) {
      refetchItems();
    }
  }, [qualityPlan]);

  const handleApprove = async () => {
    Modal.confirm({
      title: 'Approve Quality Plan',
      content: 'Are you sure you want to approve this quality plan?',
      onOk: async () => {
        try {
          await approveMutation.mutateAsync({ id: id!, notes: '' });
          refetch();
        } catch (error) {
          console.error('Error approving quality plan:', error);
        }
      },
    });
  };

  const handleAddItem = () => {
    setEditingItem(null);
    form.resetFields();
    setIsItemModalVisible(true);
  };

  const handleEditItem = (item: QualityPlanItem) => {
    setEditingItem(item);
    form.setFieldsValue({
      ...item,
      scheduled_date: item.scheduled_date ? dayjs(item.scheduled_date) : undefined,
    });
    setIsItemModalVisible(true);
  };

  const handleDeleteItem = async (itemId: string) => {
    try {
      await deleteItemMutation.mutateAsync({ planId: id!, itemId });
      refetchItems();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleItemSubmit = async () => {
    try {
      const values = await form.validateFields();
      const itemData = {
        ...values,
        scheduled_date: values.scheduled_date ? values.scheduled_date.format('YYYY-MM-DD') : undefined,
      };

      if (editingItem) {
        await updateItemMutation.mutateAsync({
          planId: id!,
          itemId: editingItem.id,
          data: itemData,
        });
      } else {
        await createItemMutation.mutateAsync({
          planId: id!,
          data: itemData,
        });
      }

      setIsItemModalVisible(false);
      form.resetFields();
      setEditingItem(null);
      refetchItems();
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Draft: 'default',
      'Under Review': 'processing',
      Approved: 'success',
      Active: 'success',
      Superseded: 'warning',
      Cancelled: 'error',
      Pending: 'default',
      Scheduled: 'processing',
      'In Progress': 'processing',
      Completed: 'success',
      Skipped: 'warning',
      'On Hold': 'orange',
    };
    return colors[status] || 'default';
  };

  const getInspectionTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'Hold Point': 'red',
      'Witness Point': 'orange',
      'Review Point': 'blue',
      Standard: 'default',
    };
    return colors[type] || 'default';
  };

  const itemColumns: ColumnsType<QualityPlanItem> = [
    {
      title: 'Seq',
      dataIndex: 'sequence_number',
      key: 'sequence_number',
      width: 60,
      sorter: (a, b) => a.sequence_number - b.sequence_number,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Type',
      dataIndex: 'inspection_type',
      key: 'inspection_type',
      width: 120,
      render: (type: string) => (
        <Tag color={getInspectionTypeColor(type)}>{type}</Tag>
      ),
      filters: [
        { text: 'Hold Point', value: 'Hold Point' },
        { text: 'Witness Point', value: 'Witness Point' },
        { text: 'Review Point', value: 'Review Point' },
        { text: 'Standard', value: 'Standard' },
      ],
      onFilter: (value, record) => record.inspection_type === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>,
      filters: [
        { text: 'Pending', value: 'Pending' },
        { text: 'Scheduled', value: 'Scheduled' },
        { text: 'In Progress', value: 'In Progress' },
        { text: 'Completed', value: 'Completed' },
        { text: 'Skipped', value: 'Skipped' },
        { text: 'On Hold', value: 'On Hold' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Scheduled Date',
      dataIndex: 'scheduled_date',
      key: 'scheduled_date',
      width: 120,
      render: (date: string) => (date ? dayjs(date).format('DD/MM/YYYY') : 'N/A'),
      sorter: (a, b) => {
        if (!a.scheduled_date) return 1;
        if (!b.scheduled_date) return -1;
        return dayjs(a.scheduled_date).unix() - dayjs(b.scheduled_date).unix();
      },
    },
    {
      title: 'Witness',
      key: 'witness',
      width: 80,
      render: (_, record) => (
        <Tag color={record.witness_required ? 'orange' : 'default'}>
          {record.witness_required ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Mandatory',
      key: 'mandatory',
      width: 80,
      render: (_, record) => (
        <Tag color={record.mandatory ? 'red' : 'default'}>
          {record.mandatory ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditItem(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete inspection item"
            description="Are you sure you want to delete this item?"
            onConfirm={() => handleDeleteItem(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!qualityPlan) {
    return <div>Quality Plan not found</div>;
  }

  const tabItems = [
    {
      key: 'overview',
      label: 'Overview',
      children: (
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Plan Number">{qualityPlan.plan_number}</Descriptions.Item>
          <Descriptions.Item label="Version">{qualityPlan.version}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={getStatusColor(qualityPlan.status)}>{qualityPlan.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Revision">{qualityPlan.revision_number}</Descriptions.Item>
          {qualityPlan.work_order && (
            <Descriptions.Item label="Work Order" span={2}>
              <Button
                type="link"
                size="small"
                onClick={() => navigate(`/manufacturing/work-orders`)}
                style={{ padding: 0 }}
              >
                {qualityPlan.work_order.work_order_number}
              </Button>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                - {qualityPlan.work_order.work_order_status}
              </Text>
            </Descriptions.Item>
          )}
          {qualityPlan.project && (
            <Descriptions.Item label="Project" span={2}>
              {qualityPlan.project.project_name}
            </Descriptions.Item>
          )}
          {qualityPlan.effective_date && (
            <Descriptions.Item label="Effective Date">
              {dayjs(qualityPlan.effective_date).format('DD/MM/YYYY')}
            </Descriptions.Item>
          )}
          {qualityPlan.expiry_date && (
            <Descriptions.Item label="Expiry Date">
              {dayjs(qualityPlan.expiry_date).format('DD/MM/YYYY')}
            </Descriptions.Item>
          )}
          {qualityPlan.approved_by && (
            <>
              <Descriptions.Item label="Approved By">{resolve(qualityPlan.approved_by)}</Descriptions.Item>
              <Descriptions.Item label="Approval Date">
                {qualityPlan.approval_date ? dayjs(qualityPlan.approval_date).format('DD/MM/YYYY') : 'N/A'}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      ),
    },
    {
      key: 'items',
      label: `Inspection Items (${items.length})`,
      children: (
        <div>
          <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
            <Text strong>Inspection Points</Text>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddItem}
              disabled={qualityPlan.status === 'Approved'}
            >
              Add Inspection Item
            </Button>
          </Space>
          <Table
            columns={itemColumns}
            dataSource={items}
            rowKey="id"
            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
            size="small"
          />
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Space style={{ marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/qms/quality-plans')}>
          Back
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          Quality Plan: {qualityPlan.plan_number?.trim() || qualityPlan.id}
        </Title>
      </Space>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic title="Status" value={qualityPlan.status} valueStyle={{ fontSize: '16px' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic
              title="Inspection Items"
              value={items.length}
              valueStyle={{ color: '#145A54' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic
              title="Completed"
              value={items.filter((i) => i.status === 'Completed').length}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card>
            <Statistic
              title="Pending"
              value={items.filter((i) => i.status === 'Pending').length}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Approval Workflow */}
      <QualityPlanApprovalWorkflow
        qualityPlan={qualityPlan}
        onSuccess={() => {
          void refetch();
        }}
        canApprove={qualityPlan.status === 'Under Review'}
      />

      <Card>
        <Tabs items={tabItems} />
      </Card>

      {/* Add/Edit Item Modal */}
      <Modal
        title={editingItem ? 'Edit Inspection Item' : 'Add Inspection Item'}
        open={isItemModalVisible}
        onCancel={() => {
          setIsItemModalVisible(false);
          form.resetFields();
          setEditingItem(null);
        }}
        onOk={handleItemSubmit}
        okText={editingItem ? 'Update' : 'Create'}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea rows={3} placeholder="Enter inspection point description" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="inspection_type"
                label="Inspection Type"
                rules={[{ required: true, message: 'Please select inspection type' }]}
              >
                <Select placeholder="Select type">
                  <Select.Option value="Hold Point">Hold Point</Select.Option>
                  <Select.Option value="Witness Point">Witness Point</Select.Option>
                  <Select.Option value="Review Point">Review Point</Select.Option>
                  <Select.Option value="Standard">Standard</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="status" label="Status" initialValue="Pending">
                <Select>
                  <Select.Option value="Pending">Pending</Select.Option>
                  <Select.Option value="Scheduled">Scheduled</Select.Option>
                  <Select.Option value="In Progress">In Progress</Select.Option>
                  <Select.Option value="Completed">Completed</Select.Option>
                  <Select.Option value="Skipped">Skipped</Select.Option>
                  <Select.Option value="On Hold">On Hold</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="acceptance_criteria" label="Acceptance Criteria">
            <TextArea rows={2} placeholder="Enter acceptance criteria" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} lg={12}>
              <Form.Item name="scheduled_date" label="Scheduled Date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="sequence_number" label="Sequence Number">
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="required_personnel_qualification" label="Required Personnel Qualification">
            <Input placeholder="e.g., ASNT Level II, API 510" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} lg={12}>
              <Form.Item name="witness_required" label="Witness Required" valuePropName="checked" initialValue={false}>
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="witness_party" label="Witness Party">
                <Select placeholder="Select witness party" allowClear>
                  <Select.Option value="Customer">Customer</Select.Option>
                  <Select.Option value="Third Party">Third Party</Select.Option>
                  <Select.Option value="Authorized Inspector">Authorized Inspector</Select.Option>
                  <Select.Option value="Regulatory">Regulatory</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} lg={12}>
              <Form.Item name="mandatory" label="Mandatory" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="can_skip" label="Can Skip" valuePropName="checked" initialValue={false}>
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default QualityPlanDetailPage;
